import Vue from 'vue';

/**
 * 将 Vue2 组件挂载到 AgentChat SDK `showTip` 提供的 DOM 容器上
 *
 * 使用场景：
 * - SDK tip 只提供挂载点 el，不感知 Vue
 * - 宿主用本方法把 EntryGuideTip / EntryWakeTip / CouponExpireTip 等组件挂进去
 *
 * 【兜底】tip 按钮 listeners（mute / gotIt 等）同步/异步抛错均吞掉，
 * destroy 失败也吞掉，避免气泡点击或收起时打断充值页
 *
 * @param {HTMLElement} el SDK 传入的 tip 挂载点
 * @param {Object} component Vue 组件选项 / SFC
 * @param {Object} [props] 传给组件的 propsData
 * @param {Object<string, Function>} [listeners]
 *   事件监听表，key 为组件 $emit 的事件名（如 'view-detail' / 'mute'）
 * @returns {Function} destroy 销毁函数；SDK hideTip 时会调用，用于 $destroy 并清理 DOM
 *
 * @example
 * chat.showTip({
 *   tipId: 'xxx',
 *   render(el) {
 *     return mountVueTip(el, CouponExpireTip, { coupon }, {
 *       mute() { chat.hideTip('xxx'); }
 *     });
 *   }
 * });
 */
export function mountVueTip(el, component, props, listeners) {
  // 先清空挂载点，避免残留上一次 tip 的 DOM
  el.innerHTML = '';
  const Ctor = Vue.extend(component);
  const instance = new Ctor({
    propsData: props || {},
  });
  // 【兜底】绑定事件：组件内 $emit('mute') -> listeners.mute（吞掉回调抛错）
  if (listeners) {
    Object.keys(listeners).forEach((event) => {
      const handler = listeners[event];
      if (typeof handler !== 'function') return;
      instance.$on(event, (...args) => {
        try {
          const result = handler(...args);
          if (result != null && typeof result.then === 'function') {
            // 【兜底】异步 listener reject 不产生 UnhandledRejection
            void result.catch((err) => {
              console.warn(
                `[PayAssistant] tip listener "${event}" async failed`,
                err,
              );
            });
          }
        } catch (err) {
          console.warn(`[PayAssistant] tip listener "${event}" failed`, err);
        }
      });
    });
  }
  // 先离线 $mount，再手动插入，避免替换掉 SDK 容器本身
  instance.$mount();
  el.appendChild(instance.$el);
  return function destroy() {
    // 【兜底】$destroy / 卸 DOM 失败不影响 SDK hideTip 后续逻辑
    try {
      instance.$destroy();
      if (instance.$el && instance.$el.parentNode) {
        instance.$el.parentNode.removeChild(instance.$el);
      }
      el.innerHTML = '';
    } catch (err) {
      console.warn('[PayAssistant] mountVueTip destroy failed', err);
    }
  };
}
